import type { GpsPoint } from '../../domain/telemetry/processed/GpsPoint'
import type { LeanAnglePoint } from '../../domain/telemetry/processed/LeanAngle'
import type { AccelerationPoint } from '../../domain/telemetry/processed/Acceleration'
import type { GForcePoint } from '../../domain/telemetry/processed/GForce'
import type { SpatialBucket } from '../../domain/telemetry/spatial/SpatialBucket'
import type { FieldKind, FieldSpec } from './columnTable'

type FieldsOf<T> = { readonly [K in keyof Required<T>]: FieldKind }

const GPS_POINT: FieldsOf<GpsPoint> = {
  latitude: 'number',
  longitude: 'number',
  altitude: 'number',
  speedKmh: 'number',
  timestamp: 'date',
  distanceMeters: 'number',
  elapsedMs: 'number',
  timeString: 'elapsedTimeString',
  heading: 'number',
}

const LEAN_ANGLE_POINT: FieldsOf<LeanAnglePoint> = {
  leanAngleDegrees: 'number',
  timestamp: 'date',
  distanceMeters: 'number',
  elapsedMs: 'number',
  speedKmh: 'number',
  source: 'text',
}

const ACCELERATION_POINT: FieldsOf<AccelerationPoint> = {
  longitudinalMs2: 'number',
  lateralMs2: 'number',
  verticalMs2: 'number',
  timestamp: 'date',
  distanceMeters: 'number',
  elapsedMs: 'number',
  source: 'text',
  speedTrendMismatch: 'boolean',
}

const G_FORCE_POINT: FieldsOf<GForcePoint> = {
  longitudinalG: 'number',
  lateralG: 'number',
  verticalG: 'number',
  combinedG: 'number',
  timestamp: 'date',
  distanceMeters: 'number',
  elapsedMs: 'number',
}

const SPATIAL_BUCKET: FieldsOf<SpatialBucket> = {
  distance: 'number',
  gpsIndex: 'number',
  elapsedMs: 'number',
  speedKmh: 'number',
  smoothedLeanAngle: 'number',
  rawLeanAngle: 'number',
  smoothedAcceleration: 'number',
  rawAcceleration: 'number',
}

function specs(fields: Record<string, FieldKind>): readonly FieldSpec[] {
  return Object.entries(fields).map(([name, kind]) => ({ name, kind }))
}

export const LAP_TABLE_FIELDS = {
  gpsPoints: specs(GPS_POINT),
  leanAngle: specs(LEAN_ANGLE_POINT),
  acceleration: specs(ACCELERATION_POINT),
  gForce: specs(G_FORCE_POINT),
  spatialBuckets: specs(SPATIAL_BUCKET),
} as const

export type LapTableName = keyof typeof LAP_TABLE_FIELDS
